import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { AgTableCard } from '../UI';

const TaskTable = ({ tasks, selectedProducts, viewMode, loading }) => {
    const navigate = useNavigate();
    const { content } = useContentfulContext();

    const getHoverMessage = (task) => {
        if (!task.is_accessible_to_org && !task.is_accessible_to_user) {
            return content.TASKS_CONTENT.hover_messages.message_1;
        }
        if (!task.is_accessible_to_org) {
            return content.TASKS_CONTENT.hover_messages.message_2;
        }
        if (!task.is_accessible_to_user) {
            return content.TASKS_CONTENT.hover_messages.message_3;
        }
        return null;
    };

    const columnDefs = useMemo(() => [
        {
            headerName: content.TASKS_CONTENT.table_headers.title,
            field: "title",
            cellRenderer: (params) => {
                const isDisabled = !params.data.is_accessible_to_org || !params.data.is_accessible_to_user;
                return (
                    <span
                        onClick={() => {
                            if (!isDisabled) {
                                navigate(`/functions/one-off-tasks/${params.data.id}`, { state: { viewMode } });
                            }
                        }}
                        className={`${
                            isDisabled
                                ? 'text-stride-muted cursor-not-allowed'
                                : 'font-medium text-primary hover:text-primary-hover hover:underline cursor-pointer'
                        }`}
                    >
                        {params.value}
                    </span>
                );
            },
            tooltipValueGetter: (params) => {
                return getHoverMessage(params.data);
            },
            sortable: true,
            filter: true,
        },
        {
            headerName: content.TASKS_CONTENT.table_headers.products,
            field: "products",
            cellRenderer: (params) => {
                const products = params.data.products || [];
                const filteredProducts = products.filter(product =>
                    selectedProducts.includes(product.id)
                );

                const renderProducts = (list) => (
                    <div className="flex flex-wrap gap-1.5 py-1">
                        {list.map((p, i) => (
                            <span key={i} className="stride-badge-pill bg-slate-100 text-slate-700 text-xs font-medium">
                                {p.name}
                            </span>
                        ))}
                    </div>
                );

                if (selectedProducts.length === 0 && products.length > 0) {
                    return renderProducts(products);
                } else if (filteredProducts.length > 0) {
                    return renderProducts(filteredProducts);
                }
                return <span className="text-stride-muted text-xs">N/A</span>;
            },
        },
        {
            headerName: content.TASKS_CONTENT.table_headers.status,
            field: "status",
            cellRenderer: (params) => {
                const isAvailable = params.data.available;
                return (
                    <span
                        className={`stride-badge-pill font-semibold text-xs ${
                            isAvailable
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-amber-50 text-amber-700'
                        }`}
                    >
                        {isAvailable ? 'Available' : 'Upcoming'}
                    </span>
                );
            },
        },
    ], [selectedProducts, navigate, content, viewMode]);

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 150,
            columnLimits: columnDefs.map((col) => ({
                colId: col.field,
                minWidth: col.minWidth || 140,
            })),
        };
    }, [columnDefs]);

    return (
        <AgTableCard
            tableId="function-table"
            loading={loading}
            rowData={tasks}
            columnDefs={columnDefs}
            defaultColDef={{
                sortable: true,
                filter: true,
            }}
            autoSizeStrategy={autoSizeStrategy}
            domLayout="autoHeight"
        />
    );
};

export default TaskTable;
